<template>
    <div data-test-id="comp-summary-panel" :class="showModal ? 'open' : 'closed'" class="side-panel-left">
        <ExpandCollapse id="collapsible-comp-summary-panel" name="compsearch-sidemodal" :is-open-initially="false" :is-vertical-center="false"
            open-state="right" closed-state="right" :show-double-chevron="true"
            @toggle-expand-collapse="toggleModal(); $emit('resize-modal')" :show-circle="true" />
        <div class="panel comp-summary" :class="!showModal ? 'hidden' : ''">
            <div class="content">
                <!-- Header -->
                <div class="header-search">
                    <div>
                        <div class="comp-summary-title left-div">Comp Set Summary</div>
                    </div>
                </div>
                <div class="divider"></div>
                <div>
                    <!-- Budget Overview -->
                    <div class="budget-overview-container" data-test-id="budget-overview-container">
                        <div class="accordion-panel-header">
                            <label for="budgetOverview">Budget Overview</label>
                            <ExpandCollapse id="collapsible-budget-overview" name="budgetOverview" :is-open-initially="true" :is-vertical-center="false"
                                open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"
                                :chevron-fill="'#4A5FD5'"
                                @toggle-expand-collapse="expandCollapseController('budgetOverview');" />
                        </div>
                        <div v-if="isBudgetOverviewOpen" id="budget-overview-content">
                            <div v-if="noSelectedCompSet && currentProjects.length > 0" class="no-selected-data">
                                No generated charts to show, please select a comp set.
                            </div>
                            <div v-if="currentProjects.length == 0 && isDataLoaded" class="no-selected-data">
                                No generated charts to show, please refine your search.
                            </div>
                            <div v-if="!isDataLoaded && currentProjects.length == 0" class="spinner-container">
                                <OverlaySpinner :overlay="false" />
                            </div>
                            <!-- REMOVED: Duplicate project display - projects are shown in "Active Comp Set" section below -->
                            
                            <!-- Scope Search Mode: Show scatter plot with trend lines -->
                            <div v-if="isDataLoaded && useScopeSearch && scopeSearchTerms.length > 0 && scopeChartData">
                                <div class="scope-search-results">
                                    <div class="scope-terms-display">
                                        <strong>Searching for scopes:</strong>
                                        <span v-for="term in scopeSearchTerms" :key="term.id" class="scope-badge">{{ term.name }}</span>
                                    </div>
                                    <div class="scope-chart-container">
                                        <div class="chart-label">Scope Budget &amp; Variance by Property</div>
                                        <ag-charts-vue
                                            v-if="scopeChartData"
                                            :options="scopeChartData"
                                            class="ag-chart scope-stacked-bar-chart"
                                        />
                                    </div>
                                    <div class="go-to-button">
                                        <button class="secondary-action-button go-to-budget-button" @click="reroute('/compsearch/budget', 'CompSearchDetails')">Go To Budget Page</button>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Normal Mode: Show doughnut and bar charts -->
                            <div v-else-if="isDataLoaded && !noSelectedCompSet && currentProjects.length > 0 && doughnutAgChartOptions && budgetVarianceAgChartOptions">
                                <div aria-title="budgetOverview" title="budgetOverview">
                                    <div class="view-by-container">
                                        <div class="custom-select label-text-spacing">
                                            <select class="right-div" id="budget-type" v-model="viewByTGU" @change="setViewMode">
                                                <option :value="VIEW_MODES_TGU.VIEW_BY_TOTAL">View By Total</option>
                                                <option :value="VIEW_MODES_TGU.VIEW_BY_GSF">View per $/GSF</option>
                                                <option :value="VIEW_MODES_TGU.VIEW_BY_UNIT">View per $/Unit</option>
                                            </select>
                                        </div>
                            
                                        <div class="custom-select label-text-spacing">
                                            <select class="right-div" id="budget-type" v-model="viewByAM">
                                                <option value="viewByAverage">View By Average</option>
                                                <option value="viewMedian">View Median</option>
                                            </select>
                                        </div>
                                    </div>
                                    <!-- AG Charts -->
                                    <div class="budget-overview-container">
                                        <div class="budget-chart-container">
                                            <!-- Total Project Costs Doughnut Chart -->
                                            <div data-test-id="doughnut-chart-content" class="doughnut-chart-container">
                                                <div class="chart-label">Total Project Costs</div>
                                                <div class="chart-content doughnut-chart-body">
                                                    <div class="ag-chart-wrapper donut-wrapper">
                                                        <ag-charts-vue
                                                            v-if="doughnutAgChartOptions"
                                                            :options="doughnutAgChartOptions"
                                                            class="ag-chart"
                                                        />
                                                        <div class="donut-center" v-if="doughnutSummaryValue">
                                                            <div class="donut-total">{{ doughnutSummaryValue }}</div>
                                                            <div class="donut-label">Total Project Costs</div>
                                                        </div>
                                                    </div>
                                                    <div class="donut-legend">
                                                        <div class="legend-row"><span class="legend-swatch acquisition"></span><span>Acquisition</span><span class="legend-value">{{ kMillionsWithDecimalsFormat(lcTotalRevisedBudget) }}</span></div>
                                                        <div class="legend-row"><span class="legend-swatch hard"></span><span>Hard Costs</span><span class="legend-value">{{ kMillionsWithDecimalsFormat(hcTotalRevisedBudget) }}</span></div>
                                                        <div class="legend-row"><span class="legend-swatch soft"></span><span>Soft Costs</span><span class="legend-value">{{ kMillionsWithDecimalsFormat(scTotalRevisedBudget) }}</span></div>
                                                        <div class="legend-row"><span class="legend-swatch misc"></span><span>Finance/Misc</span><span class="legend-value">{{ kMillionsWithDecimalsFormat(miscFinancingTotalRevisedBudget) }}</span></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Hard & Soft Cost Summary Bar Chart -->
                                            <div data-test-id="bar-chart-content" class="bar-chart-container">
                                                <div class="chart-label">Hard & Soft Cost Summary</div>
                                                <div class="ag-chart-wrapper">
                                                    <ag-charts-vue
                                                        v-if="budgetVarianceAgChartOptions"
                                                        :options="budgetVarianceAgChartOptions"
                                                        class="ag-chart"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div class="go-to-button">
                                            <button class="secondary-action-button go-to-budget-button" @click="reroute('/compsearch/budget', 'CompSearchDetails')">Go To Budget Page</button>
                                        </div>
                                    </div>
                                    <!-- HIDDEN: Schedule Overview Section -->
                                    <!-- <div data-test-id="schedule-overview-content" class="schedule-overview-container">
                                        <label>Schedule Overview</label>
                                        <div class="schedule-card">
                                            <div class="text-section">
                                                <h1>Average Durations</h1>
                                                <p>Planned</p>
                                                <p class="variance">Variance</p>
                                                <p>Actual</p>
                                            </div>
                                            <div class="numbers-section">
                                                <h1><u>Months</u></h1>
                                                <p class="planned-number">{{ numberFormat(plannedMonthsAvg, 0) }}</p>
                                                <p class="variance-number">{{ varianceMonthsAvg > 0 ? '+' + numberFormat(varianceMonthsAvg, 0) : numberFormat(varianceMonthsAvg, 0) }}</p>
                                                <p class="actual-number">{{ numberFormat(actualMonthsAvg, 0) }}</p>
                                            </div>
                                        </div>
                                        <div class="go-to-button">
                                            <button class="secondary-action-button go-to-schedule-button" @click="reroute('/compsearch/schedule', 'Schedule')">Go To Schedule Page</button>
                                        </div>
                                    </div> -->
                                </div>
                            </div>
                        </div>
                        
                    </div>
                    <div v-if="isDataLoaded" class="divider"></div>

                    <!--Active Compset-->
                    <div v-if="isDataLoaded" data-test-id="active-compset-content" class="active-compset-container">
                        <div class="accordion-panel-header header-search">
                            <div class="left-div"><label for="activeCompSet">Active Comp Set</label><span
                                    class="comp-set-counter">{{ compSetCount }}</span>
                            </div>
                            <div class="right-div"><button class="secondary-action-button disabled-button">Enlarge List
                                    View</button>
                            </div>
                            <ExpandCollapse id="collapsible-active-compset" name="activeCompSet" :is-open-initially="true" :is-vertical-center="false"
                                open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"
                                :chevron-fill="'#4A5FD5'"
                                @toggle-expand-collapse="expandCollapseController('activeCompSet');" />
                        </div>
                        <div v-if="isActiveCompSetOpen" id="active-compsent-content" aria-title="ActiveCompSet" title="ActiveCompSet">
                            <div class="active-compset-label">Additional comps exist outside search area <span
                                    class="disabled-button">Zoom
                                    Out</span>
                                for
                                more results</div>
                            <ul>
                                <li data-test-id="active-compset-checkbox-list" class="asset-images-options" v-for="(project, index) in displayedProjects" :key="index">
                                    <div class="active-compset-details-container">
                                        <!-- Primary image from documents or placeholder -->
                                        <div class="property-thumbnail">
                                        <router-link :to="`/portfolio/project-summary/${project.id}`">
                                            <img :src="getPrimaryImageUrl(project)" alt="Property Image" @error="handleImageError" />
                                        </router-link>
                                        </div>
                                        <div class="property-info">
                                            <div class="property-address">
                                                <router-link :to="`/portfolio/project-summary/${project.id}`" class="project-name-link">
                                                    <strong>{{ project.name }}</strong>
                                                </router-link>
                                                <label class="custom-checkbox">
                                                    <input type="checkbox" style="display: none;" :id="project.id"
                                                        :value="project.value" @change="toggleSelected(project.id)"
                                                        :checked="isProjectSelected(project.id)">
                                                    <span class="checkmark"></span>
                                                </label>
                                            </div>
                                            <div class="property-details">
                                                <div class="property-building-info">
                                                    <div class="property-building-label">Building
                                                        Details</div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Use Type(s)</div>
                                                        <div>{{ project.building_use_types_abv ? project.building_use_types_abv.join('; ') :
            '-' }}</div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Gross Sq Ft</div>
                                                        <div>{{ project.total_gsf ? numberFormat(project.total_gsf, 0) : '-'
                                                            }}
                                                        </div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Unit Count</div>
                                                        <div>{{ project.total_num_units ? numberFormat(project.total_num_units, 0) : '-'
                                                            }}</div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Stories</div>
                                                        <div>{{ project.num_floors ? numberFormat(project.num_floors, 0) : '-' }}</div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Parking Count</div>
                                                        <div>{{ project.parking_count ? numberFormat(project.parking_count, 0) : '-' }}</div>
                                                    </div>
                                                </div>
                                                <div class="property-construction-info">
                                                    <div class="property-construction-label">Construction
                                                        Details</div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Construction Type</div>
                                                        <div class="property-info-value ">{{ project.project_construction_types_abv ? project.project_construction_types_abv.join('; ') : '-' }}</div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Year Completed</div>
                                                        <div>{{ project.actual_end_date ?
                                                            extractDate(project.actual_end_date)
                                                            : '-' }}</div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Const. Duration (mo)</div>
                                                        <div>{{ project.project_length_months ?
                                                            parseFloat(project.project_length_months).toFixed(0) : '-' }}
                                                        </div>
                                                    </div>
                                                    <div class="property-info-item">
                                                        <div class="property-info-label">Finish Level</div>
                                                        <div>{{ project.finish_level ? project.finish_level.code : '-' }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div :class="index + 1 != currentProjects.length ? 'divider-comp-set' : ''">
                                        <div></div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="divider"></div>
                    <!-- We're only loading the ag grid on the background here to draw the dougnut chart -->
                    <div class="grid-container" id="budgetViewGrid" style="display: none;">
                        <BudgetGrid :is-auto-width="false" :shouldCalculateGrandTotal="false" :aggregation-method="viewByAM" @setChartData="setChartData"></BudgetGrid>
                    </div>
                                    <!-- Action Buttons -->
                <div class="action-btn-container">
                    <div class="left-div ">
                        <button class="secondary-action-button">Clear Selection</button>
                        <button class="secondary-action-button">Select All</button>
                    </div>
                    <div class="right-div">
                        <button class="primary-action-button"
                            @click="reroute('/compsearch/budget', 'CompSearchDetails')">See Detail</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { doughnutMiddleTextPlugin, customBarLabelColorPlugin } from "@/assets/js/chart_config";
import { currencyFormat, currencyFormatRoundedDecimal, kMillionsFormat, kMillionsWithDecimalsFormat, median, numberFormat, percentFormat } from "@/assets/js/utils";
import { useAuthStore } from '@/stores/auth_store';
import { useBudgetDataStore } from '@/stores/budget_data_store';
import { useCompSetStore } from "@/stores/comp_set_store";
import { useMathSummaryStore } from "@/stores/math_summary_store";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Colors,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import { computed, onMounted, onUpdated, reactive, ref, defineEmits, watch, nextTick } from "vue";
import { Bar, Doughnut } from "vue-chartjs";
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import ExpandCollapse from "../helpers/ExpandCollapse.vue";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import BudgetGrid from "../../components/helpers/BudgetGrid.vue";
import { VIEW_MODES_TGU } from "@/types/Constants";
import OverlaySpinner from "@/components/helpers/OverlaySpinner.vue"
import { fetchImage } from "@/assets/js/image_utils";
import { AgChartsVue } from "ag-charts-vue3";

// TODO: Comment out the Schedule AG Grid related codes
// import ToolTip from "../../components/helpers/ToolTip.vue";
// import AgGrid from "../../components/AgGrid.vue";
// import { cleanupHierarchyNames } from "@/data/data";
// import GanttCellRenderer from "../../components/renderers/GanttCellRenderer.vue";
// import { useGanttTooltipStore } from "@/stores/gantt_tooltip_store";

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    Colors,
    ArcElement,
    doughnutMiddleTextPlugin(),
    ChartDataLabels,
    customBarLabelColorPlugin()
);

// Flags for loaded Data
const showModal = ref(false); // Always start hidden - panel should only show after search
const isDataLoaded = ref(false);

// Container values for the charts
let hcTotalOriginalBudget = ref(0);
let hcTotalRevisedBudget = ref(0);
let scTotalOriginalBudget = ref(0);
let scTotalRevisedBudget = ref(0);
let miscFinancingTotalOriginalBudget = ref(0);
let miscFinancingTotalRevisedBudget = ref(0);
let lcTotalOriginalBudget = ref(0);
let lcTotalRevisedBudget = ref(0);
let contingenciesTotalOriginalBudget = ref(0);
let contingenciesTotalRevisedBudget = ref(0);
let avgTotalGSF = ref(0);
let avgTotalUnits = ref(0)

// 🔥 NEW: Store BASE values (before division by GSF/Unit) so we can recalculate when view mode changes
let baseHcTotalOriginalBudget = ref(0);
let baseHcTotalRevisedBudget = ref(0);
let baseScTotalOriginalBudget = ref(0);
let baseScTotalRevisedBudget = ref(0);
let baseMiscFinancingTotalOriginalBudget = ref(0);
let baseMiscFinancingTotalRevisedBudget = ref(0);
let baseLcTotalOriginalBudget = ref(0);
let baseLcTotalRevisedBudget = ref(0);
let baseContingenciesTotalOriginalBudget = ref(0);
let baseContingenciesTotalRevisedBudget = ref(0)

let doughnutChartPercentage = ref([]);

let perProjectGSF = ref({})

// Chart update key - no longer needed, AG Charts auto-updates from reactive refs
const chartUpdateKey = ref(0);

let isBudgetOverviewOpen = ref(true)
let isActiveCompSetOpen = ref(true)

// Reactive data
let selectedViewByTGU = ref(VIEW_MODES_TGU.VIEW_BY_TOTAL);
const viewByTGU = computed({
  get() {
    return selectedViewByTGU.value;
  },
  set(value) {
    selectedViewByTGU.value = value;
    setViewMode();
  }
});

let viewByAM = ref('viewByAverage');

// Computed property to determine if we should show project details without chart data
const shouldShowProjectsWithoutData = computed(() => {
  const hasProjects = currentProjects.value.length > 0;
  const dataLoaded = isDataLoaded.value;
  const noChartData = !doughnutAgChartOptions.value?.data?.length || !budgetVarianceAgChartOptions.value?.data?.length;
  console.log('🔥 shouldShowProjectsWithoutData:', { hasProjects, dataLoaded, noChartData, result: hasProjects && dataLoaded && noChartData });
  console.log('🔥 showModal.value:', showModal.value);
  return hasProjects && dataLoaded && noChartData;
});

// Helper function to get primary image URL from project's documents array
const getPrimaryImageUrl = (project) => {
  console.log('🖼️ getPrimaryImageUrl called for project', project.id, ', image_url:', project.image_url, ', type:', typeof project.image_url);
  
  // First check if image_url is set directly (skip if null, undefined, or empty)
  if (project.image_url && project.image_url !== '/img/property1.jpg') {
    console.log('🖼️ Using image_url for project', project.id, ':', project.image_url);
    return project.image_url;
  }
  
  // Look for primary image in documents array
  if (project.documents && Array.isArray(project.documents)) {
    const primaryDoc = project.documents.find(doc => doc.is_primary === true);
    if (primaryDoc && primaryDoc.id) {
      // Use absolute URL with current protocol and hostname
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.hostname === 'localhost' ? ':3000' : '';
      const imageUrl = `${protocol}//${hostname}${port}/api/v1/documents/${primaryDoc.id}/file`;
      console.log('🖼️ Generated image URL for project', project.id, ':', imageUrl);
      return imageUrl;
    } else {
      console.log('🖼️ No primary document found for project', project.id, ', documents:', project.documents.length);
    }
  } else {
    console.log('🖼️ No documents array for project', project.id);
  }
  
  // Default placeholder if no image found (use data URL to avoid external dependency)
  console.log('🖼️ Using placeholder for project', project.id);
  return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
};

// Handle image load errors
const handleImageError = (event) => {
  console.log('🖼️ Image load error:', event.target.src);
  event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
};

// Watch showModal changes
watch(showModal, (newVal) => {
  console.log('🔥🔥🔥 showModal CHANGED to:', newVal);
});

// Watch for changes in viewByAM to update charts
watch(viewByAM, () => {
  // Recalculate values when switching between average and median
  if (isDataLoaded.value && currentProjects.value.length > 0) {
    adjustAverages();
  }
});

const authStore = useAuthStore();
const budgetDataStore = useBudgetDataStore();
const compSetStore = useCompSetStore()
const mathSummaryStore = useMathSummaryStore()
let authToken = reactive({});
const allProjects = ref(compSetStore.getAllProjects);
const currentProjects = ref([]);
let noSelectedCompSet = ref(false);

// Scope search mode
const useScopeSearch = computed(() => compSetStore.getUseScopeSearch);
const scopeSearchTerms = computed(() => compSetStore.getScopeSearchTerms);
const scopeChartData = ref(null);
const scopeProjectIds = ref(new Set()); // IDs of projects that actually have the selected scope

// Sync scopeProjectIds (local ref) → store, but only when value actually changes
// Without the isSame guard this creates an infinite loop:
//   scopeProjectIds = new Set() → watch fires → setScopeFilteredProjectIds([]) → $subscribe
//   → syncProjects → scopeProjectIds = new Set() → watch fires again → repeat
watch(scopeProjectIds, (newSet) => {
  const newIds = Array.from(newSet);
  const currentIds = compSetStore.getScopeFilteredProjectIds;
  const isSame = newIds.length === currentIds.length && newIds.every(id => currentIds.includes(id));
  if (!isSame) {
    compSetStore.setScopeFilteredProjectIds(newIds);
  }
});

// Projects to show in active comp set — filtered to scope-matching ones when in scope mode
const displayedProjects = computed(() => {
  if (useScopeSearch.value && scopeSearchTerms.value.length > 0 && scopeProjectIds.value.size > 0) {
    return currentProjects.value.filter(p => {
      const id = String(typeof p === 'object' ? p.id : p);
      return scopeProjectIds.value.has(id);
    });
  }
  return currentProjects.value;
});

// Watch for scope search mode changes - MUST be after useScopeSearch, scopeSearchTerms, and currentProjects are defined
watch([useScopeSearch, scopeSearchTerms, currentProjects], async ([isScopeMode, terms, projects]) => {
  console.log('🔍🔍🔍 SCOPE WATCHER TRIGGERED:');
  console.log('  - isScopeMode:', isScopeMode);
  console.log('  - terms:', terms);
  console.log('  - terms.length:', terms ? terms.length : 'null');
  console.log('  - projects.length:', projects ? projects.length : 'null');
  
  if (isScopeMode && terms && terms.length > 0 && projects && projects.length > 0) {
    console.log('✅ All conditions met, fetching scope data for terms:', JSON.stringify(terms));
    await fetchScopeData();
  } else {
    console.log('❌ Scope search conditions NOT met');
  }
}, { immediate: true });

// Make this function accessible to parent components
function toggleModal() {
    showModal.value = !showModal.value;
    console.log('CompSummary modal toggled:', showModal.value);
    return showModal.value;
}

const compSetCount = computed(() => `(${displayedProjects.value.length})`)

const toggleSelected = (projectId) => {
    console.log('🔥 CompSummary: toggleSelected called for project', projectId);
    compSetStore.toggleProjectSelection(projectId)
    // Update currentProjects to reflect the selected projects
    currentProjects.value = Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]);
    // Set the number of active comp set
    noSelectedCompSet.value = (Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).length === 0)
    
    // Trigger budget data fetch for the updated selection
    const selectedProjectIds = currentProjects.value.join(',');
    const escalationQueryParam = compSetStore.getEscalation ? `&escalation=${compSetStore.getEscalation}` : '';
    console.log('🔥 CompSummary: Calling budgetDataStore.fetchBudgetData after toggle with:', selectedProjectIds, escalationQueryParam);
    budgetDataStore.fetchBudgetData(selectedProjectIds, escalationQueryParam);
}

function isProjectSelected(projectId) {
    return compSetStore.isProjectSelected(projectId);
}

const expandCollapseController = (element) => {
    switch (element) {
        case 'budgetOverview':
            isBudgetOverviewOpen.value = !isBudgetOverviewOpen.value
            break;
        case 'activeCompSet':
            isActiveCompSetOpen.value = !isActiveCompSetOpen.value
            break;
        default:
            break;
    }
}

onMounted(() => {
    authToken.value = authStore.getAuthTokenHeader
    syncProjects();
    // Enable auth to load fully before updating data (not sure if this completely makes sense)
    setTimeout(() => {
        // TODO: Comment out the Schedule AG Grid related codes
        // updateData()
    }, 0)

    // Attach the function to the window object for Cypress
    window.getDoughnutChartInstance = getDoughnutChartInstance;
    window.getBarChartInstance = getBarChartInstance;
    
    // Chart options are now computed properties - they auto-initialize and auto-update!
    console.log('🔄 CompSummary: Charts will auto-update from computed properties');
})

// The doughnut disappears when we revisit a page unless we use a :key that changes each time.
// The bar chart gets small when we revisit a page unless we use a :key that changes each time.
// If we *always* increment counter, the app gets stuck in an infinite loop on production,
//      so only update if the route has just changed.
const route = useRoute()
let lastRoute = route.name
let doughnutKey = ref(0)
let barChartKey = ref(0)
onUpdated(() => {
    if (route.name === 'Home' && lastRoute !== route.name) {
        lastRoute = route.name
        doughnutKey.value++
        barChartKey.value++
    } else if (lastRoute !== route.name) {
        // If we have different route from before, and it is not "Home",
        // update lastRoute so the counter.value++ update will trigger once the current route.name is again "Home"
        lastRoute = route.name
    }
})

// Fetch compset on update
const escalationQuery = ref(compSetStore.getEscalation)
compSetStore.$subscribe(syncProjects, { detached: false })
/**
 * Synchronizes the projects by updating the escalation query, fetching all projects from the store,
 * updating the count of the comp set, and checking if projects should be updated based on stored projects
 * and prior escalation. If updates are needed, it updates the current projects, selected projects,
 * adjusts averages, sets show markers to true, updates averages, resets data flags, fetches the comp set data,
 * and sets the view mode.
 */
async function syncProjects() {
    const priorEscalation = escalationQuery.value;
    updateEscalationQuery();

    allProjects.value = compSetStore.getAllProjects;
    const storedProjects = compSetStore.getIncludedProjects;

    if (storedProjects.length > 0) {
        showModal.value = true;
    }

    // Only fetch budget data and update project state when projects or escalation actually changed.
    // Calling fetchBudgetData unconditionally caused an infinite loop: image URL fetches mutated
    // reactive store objects → re-triggered $subscribe → fetched again → repeat.
    if (shouldUpdateProjects(storedProjects, priorEscalation)) {
        currentProjects.value = storedProjects;
        updateSelectedProjects();

        const selectedProjectIds = storedProjects.map(project => project.id).join(',');
        const escalationQueryParam = compSetStore.getEscalation ? `&escalation=${compSetStore.getEscalation}` : '';
        budgetDataStore.fetchBudgetData(selectedProjectIds, escalationQueryParam);

        await adjustAverages();
        compSetStore.setShowMarkers(true);
        updateAverages();
        setViewMode();
    }

    // Always check scope search mode after syncing — the watcher alone misses the case
    // where scope terms are already set but currentProjects didn't change (same search repeated).
    if (compSetStore.getUseScopeSearch && compSetStore.getScopeSearchTerms.length > 0 && storedProjects.length > 0) {
        await fetchScopeData();
    } else if (!compSetStore.getUseScopeSearch) {
        scopeChartData.value = null;
        scopeProjectIds.value = new Set();
    }
}
// Update the escalation query based on the current value in the store
function updateEscalationQuery() {
    escalationQuery.value = compSetStore.getEscalation ? `&escalation=${compSetStore.getEscalation}` : '';
}

// compSetCount is now a computed property — no manual update needed
// Check if the projects should be updated based on stored projects and prior escalation query

function shouldUpdateProjects(storedProjects, priorEscalation) {
    return JSON.stringify(storedProjects) !== JSON.stringify(currentProjects.value) || priorEscalation !== escalationQuery.value;
}

// Transform the current projects into a format suitable for the store
function updateSelectedProjects() {
    const transformedData = currentProjects.value.reduce((acc, item) => {
        acc[item.id] = compSetStore.isProjectSelected(item.id);
        return acc;
    }, {});

    // Update the selected projects in the store
    compSetStore.setSelectedProjects(transformedData);
    compSetStore.selectedProjects = transformedData;
}

// Update the average total GSF and total units from the math summary store
function updateAverages() {
    avgTotalGSF.value = mathSummaryStore.getAvgTotalGSF;
    avgTotalUnits.value = mathSummaryStore.getAvgTotalUnits;
}

// Schedule Overview Averages
let plannedMonthsAvg = ref(0);
let varianceMonthsAvg = ref(0)
let actualMonthsAvg = ref(0);

// Define a helper function to safely parse date strings, returning null for invalid inputs
const parseDateString = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
};

// Adjust the Comp Set Averages
// Used to pre-store averages of active compset
// Fetch scope data for scatter plot
const fetchScopeData = async () => {
  try {
    // currentProjects can be full objects {id, name...} OR plain ID strings depending on code path
    const projectIds = currentProjects.value.map(p => typeof p === 'object' ? p.id : p).filter(Boolean);
    const termIds = scopeSearchTerms.value.map(t => t.id);
    
    console.log('📊 Fetching scope data for projects:', projectIds, 'and terms:', termIds);
    
    // Fetch scope cost data from backend
    const response = await axios.post('/api/v1/projects/scope_costs', {
      project_ids: projectIds,
      dda_term_ids: termIds
    });
    
    if (response.data && response.data.length > 0) {
      scopeProjectIds.value = new Set(response.data.map(item => String(item.project_id)));
      generateScopeStackedBarChart(response.data);
    } else {
      console.warn('⚠️ No scope data returned from backend');
      scopeChartData.value = null;
      scopeProjectIds.value = new Set();
    }
  } catch (error) {
    console.error('❌ Error fetching scope data:', error);
    scopeChartData.value = null;
    scopeProjectIds.value = new Set();
  }
};

// Generate stacked bar chart: original budget (base) + variance (stacked) per property
// One scope at a time — x = property name, y = original_budget stacked with variance
const generateScopeStackedBarChart = (scopeData) => {
  const formatCurrency = (v) => {
    if (Math.abs(v) >= 1000000) return '$' + (v / 1000000).toFixed(2) + 'M';
    if (Math.abs(v) >= 1000)    return '$' + (v / 1000).toFixed(0) + 'K';
    return '$' + Math.round(v).toLocaleString();
  };

  // Only show properties that ACTUALLY have data for the selected scope
  // (backend already filters, but deduplicate by project in case of multiple term matches)
  const byProject = {};
  scopeData.forEach(item => {
    const key = item.project_name || `Project ${item.project_id}`;
    if (!byProject[key]) {
      byProject[key] = {
        property:          key,
        original_budget:   0,
        current_budget:    0,
        variance_over:     0, // positive variance (current > original)
        variance_under:    0, // negative variance as positive number (current < original)
        scope_name:        item.scope_name
      };
    }
    const v = Number(item.variance) || 0;
    byProject[key].original_budget += Number(item.original_budget) || 0;
    byProject[key].current_budget  += Number(item.current_budget)  || 0;
    if (v >= 0) byProject[key].variance_over  += v;
    else        byProject[key].variance_under += Math.abs(v);
  });

  const data = Object.values(byProject);
  data.sort((a, b) => b.original_budget - a.original_budget);

  const scopeName = data[0]?.scope_name || 'Scope';

  scopeChartData.value = {
    data,
    series: [
      {
        type: 'column',
        xKey: 'property',
        yKey: 'original_budget',
        yName: 'Original Budget',
        stacked: true,
        fill: '#4A5FD5',
        stroke: '#3949AB',
        tooltip: {
          renderer: (params) => ({
            title: params.datum.property,
            content: `Original Budget: ${formatCurrency(params.datum.original_budget)}\nCurrent Budget: ${formatCurrency(params.datum.current_budget)}`
          })
        }
      },
      {
        type: 'column',
        xKey: 'property',
        yKey: 'variance_over',
        yName: 'Over Budget',
        stacked: true,
        fill: '#E8A838',
        stroke: '#C8880A',
        tooltip: {
          renderer: (params) => ({
            title: params.datum.property,
            content: `Over Original: ${formatCurrency(params.datum.variance_over)}`
          })
        }
      },
      {
        type: 'column',
        xKey: 'property',
        yKey: 'variance_under',
        yName: 'Under Budget',
        stacked: true,
        fill: '#52C97A',
        stroke: '#3DAB60',
        tooltip: {
          renderer: (params) => ({
            title: params.datum.property,
            content: `Under Original: ${formatCurrency(params.datum.variance_under)}`
          })
        }
      }
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        title: { text: 'Property', enabled: true },
        paddingInner: 0.5,
        paddingOuter: 0.3,
        label: {
          rotation: -35,
          formatter: (params) => {
            const s = String(params.value);
            return s.length > 20 ? s.slice(0, 18) + '…' : s;
          }
        }
      },
      {
        type: 'number',
        position: 'left',
        title: { text: `${scopeName} Budget ($)`, enabled: true },
        label: {
          formatter: (params) => {
            const v = params.value;
            if (Math.abs(v) >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
            if (Math.abs(v) >= 1000)    return '$' + (v / 1000).toFixed(0) + 'K';
            return '$' + v;
          }
        }
      }
    ],
    legend: { enabled: true, position: 'bottom' }
  };

  console.log('✅ Scope stacked bar chart generated:', scopeChartData.value);
};

const adjustAverages = () => {
    // GSF and Units
    const newTotalGSF = [];
    const newNumUnits = []

    if (compSetStore.getIncludedProjects.length > 0) {
        for (const project of currentProjects.value) {
            // Check if the project is selected
            if (compSetStore.selectedProjects[project.id]) {
                // TODO : For now, we default to 1 if value for that property is non-existent
                newTotalGSF.push(project.total_gsf || 1);
                newNumUnits.push(project.total_num_units || 1);
                // Note : This calculation of per project GSF is not actively in use right now but might be reverted to later on
                perProjectGSF.value[project.id] = project.total_gsf || 1; 
            }
        }
    }
    avgTotalGSF.value = getAverage(newTotalGSF);
    avgTotalUnits.value = getAverage(newNumUnits);
    mathSummaryStore.setAvgTotalGSF(avgTotalGSF.value)
    mathSummaryStore.setPerProjectGSF(perProjectGSF.value)
    mathSummaryStore.setAvgTotalUnits(avgTotalUnits.value)

    let plannedMonthsArray = [];
    let varianceMonthsArray = [];
    let actualMonthsArray = [];
    if (compSetStore.getIncludedProjects.length > 0) {
        for (const project of currentProjects.value) {
            // Check if the project is selected
            if (compSetStore.selectedProjects[project.id]) {
                // Check if date strings are not null before converting to Date objects
                const actualStartDate = project.actual_start_date ? parseDateString(project.actual_start_date) : null;
                const actualEndDate = project.actual_end_date ? parseDateString(project.actual_end_date) : null;
        
                const plannedStartDate = project.planned_start_date ? parseDateString(project.planned_start_date) : null;
                const plannedEndDate = project.planned_end_date ? parseDateString(project.planned_end_date) : null;
        
                // Check if both actual and planned dates are available
                if (actualStartDate && actualEndDate && plannedStartDate && plannedEndDate) {
                    // Calculate the difference in months between actualEndDate and actualStartDate
                    const actualProjectMonths = (actualEndDate.getFullYear() - actualStartDate.getFullYear()) * 12 + actualEndDate.getMonth() - actualStartDate.getMonth();
                    actualMonthsArray.push(actualProjectMonths);
        
                    // Calculate the difference in months between plannedEndDate and plannedStartDate
                    const plannedProjectMonths = (plannedEndDate.getFullYear() - plannedStartDate.getFullYear()) * 12 + plannedEndDate.getMonth() - plannedStartDate.getMonth();
                    plannedMonthsArray.push(plannedProjectMonths);
        
                    // Calculate scheduleVariance
                    const scheduleVariance = plannedProjectMonths === 0 ? 0 : (actualProjectMonths - plannedProjectMonths);
                    varianceMonthsArray.push(scheduleVariance);
                }
            }
        }
        
        plannedMonthsAvg.value = plannedMonthsArray.length > 0 ? getAverage(plannedMonthsArray) : 0;
        actualMonthsAvg.value = actualMonthsArray.length > 0 ? getAverage(actualMonthsArray) : 0;
        varianceMonthsAvg.value = varianceMonthsArray.length > 0 ? getAverage(varianceMonthsArray) : 0;
    } else {
        // Reset to zero if none is selected
        plannedMonthsAvg.value = 0;
        actualMonthsAvg.value = 0;
        varianceMonthsAvg.value = 0;
    }
    
    // 🔥 NEW: When average/median changes, we need to update the view mode divisor and recalculate
    setViewMode();
}

// Calculate the average or median of numbers inside an array based on the selected view mode
const getAverage = (numbers) => {
    if (!numbers || numbers.length === 0) return 0;
    
    // If viewByAM is set to median, use the median function
    if (viewByAM.value === 'viewMedian') {
        return median(numbers);
    } else {
        // Otherwise calculate the average
        const sum = numbers.reduce((acc, number) => acc + number, 0);
        return sum / numbers.length;
    }
}

// Doughnut Chart
let lcTotalVariance = ref(0);
let hcTotalVariance = ref(0);
let scTotalVariance = ref(0);
let miscFinancingTotalVariance = ref(0);
let contingenciesTotalVariance = ref(0);
let lcVariancePercent = ref(0);
let hcVariancePercent = ref(0);
let scVariancePercent = ref(0);
let miscFinancingVariancePercent = ref(0);
let contingenciesVariancePercent = ref(0);
let doughnutChartData = ref([])
let viewModeLabel=ref('')

// NOTE: This used to listen to the budgetDataStore, but as of 1/2025,
// this was part of an infinite loop, so we use emits instead.
// We receive emitted data, so don't need to use the store here.
let chartData = null
function setChartData(newChartData) {
    console.log('🔥 CompSummary: setChartData called with:', newChartData);
    chartData = newChartData

    // Always set isDataLoaded to true, even if there's no data
    // This ensures the UI shows either the charts or the "no data" message
    if (!chartData || chartData.length === 0) {
        console.log('🔥 CompSummary: No chart data available, setting isDataLoaded = true');
        isDataLoaded.value = true;
        return;
    }

    // 🔥 SIMPLIFIED: Just find by DDA term ID (all projects use same total row IDs)
    // 11633 = Acquisition Costs Total
    // 11635 = Hard Costs Total
    // 11637 = Soft Costs Total
    // 11639 = Financing Costs Total
    // 12330 = Contingencies Total
    // 11642 = Project Total
    console.log('🔥 CompSummary: Looking for total rows by DDA term ID in', chartData.length, 'rows');
    
    const acquisitionTotal = chartData.find(r => r.dda_term_id === 11633);
    const hcTotal = chartData.find(r => r.dda_term_id === 11635);
    const scTotal = chartData.find(r => r.dda_term_id === 11637);
    const financingTotal = chartData.find(r => r.dda_term_id === 11639);
    const contingenciesTotal = chartData.find(r => r.dda_term_id === 12330);
    const projectTotal = chartData.find(r => r.dda_term_id === 11642);
    
    console.log('🔥 CompSummary: Found total rows by ID:', {
        acquisition: acquisitionTotal ? `✅ ${acquisitionTotal.dda_term_name}` : '❌ NOT FOUND',
        hardCosts: hcTotal ? `✅ ${hcTotal.dda_term_name}` : '❌ NOT FOUND',
        softCosts: scTotal ? `✅ ${scTotal.dda_term_name}` : '❌ NOT FOUND',
        financing: financingTotal ? `✅ ${financingTotal.dda_term_name}` : '❌ NOT FOUND',
        contingencies: contingenciesTotal ? `✅ ${contingenciesTotal.dda_term_name}` : '❌ NOT FOUND',
        projectTotal: projectTotal ? `✅ ${projectTotal.dda_term_name}` : '❌ NOT FOUND'
    });
    
    // 🔥 UPDATED: Store BASE values (raw totals) and then apply divisor for display
    // First, store the base values (these are the raw totals/averages from BudgetGrid)
    // 🔥 CRITICAL FIX: Backend returns strings, must parse to numbers for variance calculation
    baseLcTotalRevisedBudget.value = acquisitionTotal?.aggData?.current_budget ? Number(acquisitionTotal.aggData.current_budget) : 0;
    baseHcTotalRevisedBudget.value = hcTotal?.aggData?.current_budget ? Number(hcTotal.aggData.current_budget) : 0;
    baseScTotalRevisedBudget.value = scTotal?.aggData?.current_budget ? Number(scTotal.aggData.current_budget) : 0;
    baseMiscFinancingTotalRevisedBudget.value = financingTotal?.aggData?.current_budget ? Number(financingTotal.aggData.current_budget) : 0;
    baseContingenciesTotalRevisedBudget.value = contingenciesTotal?.aggData?.current_budget ? Number(contingenciesTotal.aggData.current_budget) : 0;

    baseLcTotalOriginalBudget.value = acquisitionTotal?.aggData?.original_budget ? Number(acquisitionTotal.aggData.original_budget) : 0;
    baseHcTotalOriginalBudget.value = hcTotal?.aggData?.original_budget ? Number(hcTotal.aggData.original_budget) : 0;
    baseScTotalOriginalBudget.value = scTotal?.aggData?.original_budget ? Number(scTotal.aggData.original_budget) : 0;
    baseMiscFinancingTotalOriginalBudget.value = financingTotal?.aggData?.original_budget ? Number(financingTotal.aggData.original_budget) : 0;
    baseContingenciesTotalOriginalBudget.value = contingenciesTotal?.aggData?.original_budget ? Number(contingenciesTotal.aggData.original_budget) : 0;
    
    console.log('🔥🔥 CompSummary: AFTER parsing - HC orig:', baseHcTotalOriginalBudget.value, 'HC curr:', baseHcTotalRevisedBudget.value, 'HC variance:', baseHcTotalRevisedBudget.value - baseHcTotalOriginalBudget.value);

    // Now get the current divisor and apply it to get the display values
    const divisor = mathSummaryStore.getViewModeDivisor || 1;
    
    lcTotalRevisedBudget.value = baseLcTotalRevisedBudget.value / divisor;
    hcTotalRevisedBudget.value = baseHcTotalRevisedBudget.value / divisor;
    scTotalRevisedBudget.value = baseScTotalRevisedBudget.value / divisor;
    miscFinancingTotalRevisedBudget.value = baseMiscFinancingTotalRevisedBudget.value / divisor;
    contingenciesTotalRevisedBudget.value = baseContingenciesTotalRevisedBudget.value / divisor;

    lcTotalOriginalBudget.value = baseLcTotalOriginalBudget.value / divisor;
    hcTotalOriginalBudget.value = baseHcTotalOriginalBudget.value / divisor;
    scTotalOriginalBudget.value = baseScTotalOriginalBudget.value / divisor;
    miscFinancingTotalOriginalBudget.value = baseMiscFinancingTotalOriginalBudget.value / divisor;
    contingenciesTotalOriginalBudget.value = baseContingenciesTotalOriginalBudget.value / divisor;
    
    console.log('🔥 CompSummary: Extracted budget values:', {
        hc: { original: hcTotalOriginalBudget.value, current: hcTotalRevisedBudget.value, variance: hcTotalRevisedBudget.value - hcTotalOriginalBudget.value },
        sc: { original: scTotalOriginalBudget.value, current: scTotalRevisedBudget.value, variance: scTotalRevisedBudget.value - scTotalOriginalBudget.value },
        acquisition: { original: lcTotalOriginalBudget.value, current: lcTotalRevisedBudget.value },
        financing: { original: miscFinancingTotalOriginalBudget.value, current: miscFinancingTotalRevisedBudget.value },
        contingencies: { original: contingenciesTotalOriginalBudget.value, current: contingenciesTotalRevisedBudget.value }
    });
    console.log('🔥 CompSummary: FULL aggData for HC Total:', hcTotal?.aggData);
    console.log('🔥 CompSummary: FULL aggData for SC Total:', scTotal?.aggData);

    lcTotalVariance.value = lcTotalRevisedBudget.value - lcTotalOriginalBudget.value;
    hcTotalVariance.value = hcTotalRevisedBudget.value - hcTotalOriginalBudget.value;
    scTotalVariance.value = scTotalRevisedBudget.value - scTotalOriginalBudget.value;
    miscFinancingTotalVariance.value = miscFinancingTotalRevisedBudget.value - miscFinancingTotalOriginalBudget.value;
    contingenciesTotalVariance.value = contingenciesTotalRevisedBudget.value - contingenciesTotalOriginalBudget.value;

    lcVariancePercent.value = lcTotalOriginalBudget.value > 0 ? (lcTotalRevisedBudget.value - lcTotalOriginalBudget.value) / lcTotalOriginalBudget.value : 0;
    hcVariancePercent.value = hcTotalOriginalBudget.value > 0 ? (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) / hcTotalOriginalBudget.value : 0;
    scVariancePercent.value = scTotalOriginalBudget.value > 0 ? (scTotalRevisedBudget.value - scTotalOriginalBudget.value) / scTotalOriginalBudget.value : 0;
    miscFinancingVariancePercent.value = miscFinancingTotalOriginalBudget.value > 0 ? (miscFinancingTotalRevisedBudget.value - miscFinancingTotalOriginalBudget.value) / miscFinancingTotalOriginalBudget.value : 0;
    contingenciesVariancePercent.value = contingenciesTotalOriginalBudget.value > 0 ? (contingenciesTotalRevisedBudget.value - contingenciesTotalOriginalBudget.value) / contingenciesTotalOriginalBudget.value : 0;
    
    console.log('🔥🔥🔥 CompSummary: VARIANCE PERCENTS CALCULATED:', {
        hcVariancePercent: hcVariancePercent.value,
        scVariancePercent: scVariancePercent.value,
        hcVariancePercentDisplay: (hcVariancePercent.value * 100).toFixed(2) + '%',
        scVariancePercentDisplay: (scVariancePercent.value * 100).toFixed(2) + '%'
    });

    doughnutChartData.value = [
        lcTotalRevisedBudget.value,
        hcTotalRevisedBudget.value,
        scTotalRevisedBudget.value,
        miscFinancingTotalRevisedBudget.value,
        contingenciesTotalRevisedBudget.value
    ]

    const doughnutChartDataArray = [
        lcTotalRevisedBudget.value,
        hcTotalRevisedBudget.value,
        scTotalRevisedBudget.value,
        miscFinancingTotalRevisedBudget.value,
        contingenciesTotalRevisedBudget.value
    ]

    console.log('🔥 CompSummary: Chart values set:', {
        hc: hcTotalRevisedBudget.value,
        sc: scTotalRevisedBudget.value,
        acquisition: lcTotalRevisedBudget.value,
        financing: miscFinancingTotalRevisedBudget.value
    });

    // Loop through the dougnutChartData
    const totalSumOfDoughnut = doughnutChartDataArray.reduce((a, b) => { return a + b; }).toFixed(0)
    doughnutChartPercentage.value = []
    doughnutChartDataArray.forEach(val => {
        const percentage = val * 100 / totalSumOfDoughnut;
        const roundedPercentage = Math.round(percentage * 100) / 100
        doughnutChartPercentage.value.push(`${roundedPercentage.toFixed(0)}%`);
    });

    isDataLoaded.value = true
    
    // ✅ NO NEED TO REBUILD CHARTS! AG Charts automatically updates when the reactive refs change.
    // The chart builders read from refs like hcTotalRevisedBudget.value, so when those refs
    // change (which they do above), AG Charts detects it and auto-updates.
    // This is why "View By" dropdown works - it just changes the refs!
    console.log('🔥 CompSummary: Chart data updated - AG Charts will auto-update from reactive refs');
}

let totalRevisedBudget = computed(() => {
    // console.log('here', kMillionsFormat(lcTotalRevisedBudget.value), kMillionsFormat(scTotalRevisedBudget.value), kMillionsFormat(hcTotalRevisedBudget.value), kMillionsFormat(miscFinancingTotalRevisedBudget.value), kMillionsFormat(contingenciesTotalRevisedBudget.value))
    return lcTotalRevisedBudget.value + scTotalRevisedBudget.value + hcTotalRevisedBudget.value +
        miscFinancingTotalRevisedBudget.value + contingenciesTotalRevisedBudget.value
})
let doughnutViewMode = ref('total') // this toggles between total view and per GSF
let doughnutLabel = computed(() => {
  if (doughnutViewMode.value === 'total') {
    return 'Total Project Costs'
  } else if (doughnutViewMode.value === 'gsf') {
     return 'Avg. $/GSF'
  } else if (doughnutViewMode.value === 'unit'){
    return 'Avg. $/Unit'
  } else {
    return 'Total Project Costs'
  }
})
let doughnutSummaryValue = computed(() => {
    return kMillionsWithDecimalsFormat(totalRevisedBudget.value)
})

const doughnutData = computed(() => {
    return {
        labels: ["Acquisition ", "Hard", "Soft", "Misc/Finance", "Contingencies"],
        datasets: [
            {
                backgroundColor: [
                    "#52596D", // $darkGray1
                    "#4A5FD5", // $brightBlue1
                    "#4A8FD0", // $mediumBlue1
                    "#8A92A9", // $mediumGray1
                ],
                data: doughnutChartData.value
            },
        ],
    };
});

const doughnutOptions = computed(() => {
    let json = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: 70,
        plugins: {
            title: {
                text: 'Test',
                align: "center",
                display: false,
            },
            legend: {
                labels: {
                    boxWidth: 10,
                },
                font: {
                    size: 10,
                    family: "Inter",
                },
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let actual = 0;
                        if (context.label?.includes("Hard")) {
                            actual = currencyFormat(hcTotalRevisedBudget.value)
                        } else if (context.label?.includes("Soft")) {
                            actual = currencyFormat(scTotalRevisedBudget.value)
                        } else if (context.label?.includes("Acquisition")) {
                            actual = currencyFormat(lcTotalRevisedBudget.value)
                        } else if (context.label?.includes("Misc")) {
                            actual = currencyFormat(miscFinancingTotalRevisedBudget.value)
                        } else if (context.label?.includes("Contingencies")) {
                            actual = currencyFormat(contingenciesTotalRevisedBudget.value)
                        }
                        return actual;
                    },
                    footer: function (context) {
                        let variancePerc = 0;
                        let varianceVal = 0;
                        if (context[0].label?.includes("Hard")) {
                            varianceVal = kMillionsFormat(hcTotalRevisedBudget.value - hcTotalOriginalBudget.value);
                            variancePerc = percentFormat(
                                (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
                                (hcTotalOriginalBudget.value || 1)
                            );
                        } else if (context[0].label?.includes("Soft")) {
                            varianceVal = kMillionsFormat(scTotalRevisedBudget.value - scTotalOriginalBudget.value);
                            variancePerc = percentFormat(
                                (scTotalRevisedBudget.value - scTotalOriginalBudget.value) /
                                (scTotalOriginalBudget.value || 1)
                            );
                        } else if (context[0].label?.includes("Acquisition")) {
                            varianceVal = kMillionsFormat(lcTotalRevisedBudget.value - lcTotalOriginalBudget.value);
                            variancePerc = percentFormat(
                                (lcTotalRevisedBudget.value - lcTotalOriginalBudget.value) /
                                (lcTotalOriginalBudget.value || 1)
                            );
                        } else if (context[0].label?.includes("Misc")) {
                            varianceVal = kMillionsFormat(
                                miscFinancingTotalRevisedBudget.value -
                                miscFinancingTotalOriginalBudget.value
                            );
                            variancePerc = percentFormat(
                                (miscFinancingTotalRevisedBudget.value -
                                    miscFinancingTotalOriginalBudget.value) /
                                (miscFinancingTotalOriginalBudget.value || 1)
                            );
                        } else if (context[0].label?.includes("Contingencies")) {
                            varianceVal = kMillionsFormat(
                                contingenciesTotalRevisedBudget.value -
                                contingenciesTotalOriginalBudget.value
                            );
                            variancePerc = percentFormat(
                                (contingenciesTotalRevisedBudget.value -
                                    contingenciesTotalOriginalBudget.value) /
                                (contingenciesTotalOriginalBudget.value || 1)
                            );
                        }
                        return `Variance: ${varianceVal} (${variancePerc})`
                    },
                },
            },
            doughnutMiddleTextPlugin: {
                middleTextLabel: doughnutLabel.value,
                middleTextValue: doughnutSummaryValue.value,
                fontSize: 12
            },
            datalabels: {
                display: false
            }
        },
    }

    return json
});

const barChartData = computed(() => {
    let hcChangeInBudget = hcTotalRevisedBudget.value - hcTotalOriginalBudget.value
    if (hcChangeInBudget < 0) {
        hcChangeInBudget = 0
    }
    let scChangeInBudget = scTotalRevisedBudget.value - scTotalOriginalBudget.value
    if (scChangeInBudget < 0) {
        scChangeInBudget = 0
    }
    return {
        labels: [
            ['Total HC', kMillionsWithDecimalsFormat(hcTotalRevisedBudget.value) + viewModeLabel.value],
            ['Total SC', kMillionsWithDecimalsFormat(scTotalRevisedBudget.value) + viewModeLabel.value],
        ],
        datasets: [
            {
                label: "Original Budget",
                backgroundColor: ["#4A5FD5", "#4A8FD0"],
                data: [hcTotalOriginalBudget.value, scTotalOriginalBudget.value],
            },
            {
                label: "Budget Variance",
                backgroundColor: ["#84CADE", "#84CADE"],
                data: [hcChangeInBudget, scChangeInBudget],
            },
        ]
    }
})
const barChartOptions = computed(() => {
    let json = {
        responsive: true,
        maintainAspectRatio: false,
        barPercentage: 0.8,
        layout: {
            padding: {
                top: 20, // Adjust top padding as needed
            },
        },
        scales: {
            x: {
                stacked: true,
                display: true,
                ticks: {
                    font: {
                        size: '12',
                        family: 'Inter-Bold'
                    },
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    color: 'transparent', // Makes default labels invisible
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
            y: {
                stacked: true,
                display: true,
                ticks: {
                    beginAtZero: true,
                    font: {
                        size: '10',
                        family: 'Inter',
                    },
                    min: 0,
                    max: 1000000000,
                    autoSkip: false,
                    callback: function (label) {
                        return kMillionsWithDecimalsFormat(label)
                    },
                },
                grid: {
                    drawOnChartArea: true,
                },
            },
        },
        plugins: {
            datalabels: {
                color: 'black',
                font: {
                    size: 12
                },
                anchor: 'end',
                align: 'top',
                formatter: function (val, ctx) {
                    // Get only values for Budget Variance
                    if (ctx.dataIndex === 0 && ctx.datasetIndex === 1) {
                        return `${Math.round(parseFloat((
                        (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
                        hcTotalOriginalBudget.value
                        )).toFixed(2) * 100)}%`
                    } else if (ctx.dataIndex === 1 && ctx.datasetIndex === 1) {
                        return `${Math.round(parseFloat((
                        (scTotalRevisedBudget.value - scTotalOriginalBudget.value) /
                        scTotalOriginalBudget.value
                        )).toFixed(2) * 100)}%`
                    } else {
                        return ''
                    }
                }
            },
            title: {
                text: 'Test',
                align: "center",
                display: false,
            },
            legend: {
                labels: {
                    boxWidth: 10,
                },
                font: {
                    size: 10,
                    family: "Inter",
                },
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label;
                        const value = context.parsed.y;
                        let multilineTooltip = [label + ":"];
                        multilineTooltip.push(currencyFormatRoundedDecimal(value));
                        return multilineTooltip;
                    },
                    footer: function (context) {
                        const index = context[0].dataIndex;
                        let value = null;
                        if (index == 0) {
                            value = "Variance: " +
                                percentFormat(
                                    (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) /
                                    hcTotalOriginalBudget.value
                                )
                        } else if (index == 1) {
                            value = (
                                "Variance: " +
                                percentFormat(
                                    (scTotalRevisedBudget.value - scTotalOriginalBudget.value) /
                                    scTotalOriginalBudget.value
                                )
                            )
                        }
                        return value
                    }
                }
            },
            customBarLabelColor: {
                className: 'summary-bar-canvas'
            }
        },
        
    }

    return json
})

// AG Charts Configuration - Total Project Costs Doughnut Chart
// Computed donut chart options - automatically rebuilds when reactive refs change
const doughnutAgChartOptions = computed(() => {
  console.log('🍩🍩🍩 doughnutAgChartOptions computed - auto-updating from reactive refs');
  
  // Calculate total first
  const rawData = [
    { category: 'Acquisition Costs', value: lcTotalRevisedBudget.value, color: '#52596D' },
    { category: 'Hard Costs', value: hcTotalRevisedBudget.value, color: '#4A5FD5' },
    { category: 'Soft Costs', value: scTotalRevisedBudget.value, color: '#4A8FD0' },
    { category: 'Finance/Misc Costs', value: miscFinancingTotalRevisedBudget.value, color: '#8A92A9' },
    { category: 'Contingencies', value: contingenciesTotalRevisedBudget.value, color: '#d3d3d3' }
  ].filter(d => d.value > 0);

  const total = rawData.reduce((sum, d) => sum + d.value, 0);

  // Add formatted label to each data item
  const data = rawData.map(d => {
    const pct = total > 0 ? (d.value / total * 100) : 0;
    let formattedValue = '';
    if (d.value >= 1000000) formattedValue = `$${(d.value / 1000000).toFixed(1)}M`;
    else if (d.value >= 1000) formattedValue = `$${(d.value / 1000).toFixed(0)}K`;
    else formattedValue = `$${d.value.toFixed(0)}`;
    
    return {
      ...d,
      label: pct >= 8 ? formattedValue : '',  // Only show label if big enough
      pctLabel: pct >= 8 ? `${pct.toFixed(0)}%` : ''
    };
  });

  return {
    data: data,
    // IMPORTANT: Keep this in sync with `.donut-wrapper` dimensions so the chart cannot clip.
    height: 180,
    width: 180,
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    },
    tooltip: {
      enabled: true
    },
    series: [{
      type: 'pie',
      angleKey: 'value',
      legendItemKey: 'category',
      innerRadiusRatio: 0.58,
      fills: data.map(d => d.color),
      strokes: data.map(d => d.color),
      strokeWidth: 1,
      tooltip: {
        renderer: (params) => ({
          title: params.datum.category,
          content: kMillionsWithDecimalsFormat(params.datum.value)
        })
      },
      calloutLabel: {
        enabled: false
      },
      sectorLabel: {
        enabled: false
      }
    }],
    background: {
      fill: 'transparent'
    },
    legend: {
      enabled: false
    }
  };
});

// Computed bar chart options - automatically rebuilds when reactive refs change
const budgetVarianceAgChartOptions = computed(() => {
  console.log('🎨🎨🎨 budgetVarianceAgChartOptions computed - auto-updating from reactive refs');
  console.log('🎨🎨🎨 Current budget values - HC orig:', hcTotalOriginalBudget.value, 'HC curr:', hcTotalRevisedBudget.value);
  
  // Stacked bar: Original Budget (dark blue) + Overage (light blue stacked on top)
  // Only show Hard Costs and Soft Costs
  const hcOverage = (typeof hcTotalVariance?.value === 'number' && !isNaN(hcTotalVariance.value))
    ? hcTotalVariance.value
    : (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value);
  const scOverage = (typeof scTotalVariance?.value === 'number' && !isNaN(scTotalVariance.value))
    ? scTotalVariance.value
    : (scTotalRevisedBudget.value - scTotalOriginalBudget.value);

  // IMPORTANT: hcVariancePercent/scVariancePercent are stored as DECIMALS (0.05 for 5%), so multiply by 100
  const hcVariancePct = (typeof hcVariancePercent?.value === 'number' && !isNaN(hcVariancePercent.value))
    ? hcVariancePercent.value * 100
    : (hcTotalOriginalBudget.value > 0 ? (hcOverage / hcTotalOriginalBudget.value) * 100 : 0);
  const scVariancePct = (typeof scVariancePercent?.value === 'number' && !isNaN(scVariancePercent.value))
    ? scVariancePercent.value * 100
    : (scTotalOriginalBudget.value > 0 ? (scOverage / scTotalOriginalBudget.value) * 100 : 0);
  
  console.log('🎨🎨🎨 Chart computed - hcVariancePct:', hcVariancePct, 'scVariancePct:', scVariancePct);
  console.log('🎨🎨🎨 Chart computed - hcVariancePercent.value:', hcVariancePercent.value, 'scVariancePercent.value:', scVariancePercent.value);

  const data = [
    { 
      category: 'Total HC',
      originalBudget: hcTotalOriginalBudget.value || 0,
      overage: hcOverage,
      variance: hcOverage,
      variancePct: hcVariancePct
    },
    { 
      category: 'Total SC',
      originalBudget: scTotalOriginalBudget.value || 0,
      overage: scOverage,
      variance: scOverage,
      variancePct: scVariancePct
    }
  ]; // Always show both bars

  // Add a "total" field for labeling - this is what we display at end of bar
  const dataWithTotal = data.map(d => ({
    ...d,
    total: d.originalBudget + d.overage
  }));

  return {
    data: dataWithTotal,
    height: 280,
    width: 320,
    padding: {
      top: 50,
      right: 40,
      bottom: 60,
      left: 45
    },
    tooltip: {
      enabled: true
    },
    series: [
      {
        type: 'column',
        xKey: 'category',
        yKey: 'originalBudget',
        yName: 'Original Budget',
        stacked: true,
        grouped: false, // CRITICAL: actually stack on the same x position
        fill: '#4A5FD5',
        fillOpacity: 1.0,
        stroke: '#4A5FD5',
        strokeWidth: 0,
        maxWidth: 28,
        tooltip: {
          renderer: (params) => {
            const original = params.datum.originalBudget;
            const current = original + params.datum.overage;
            const variance = params.datum.variance;
            const variancePct = params.datum.variancePct;
            const sign = variance >= 0 ? '+' : '';
            
            return {
              title: params.datum.category,
              content: [
                `Original: ${kMillionsWithDecimalsFormat(original)}`,
                `Current: ${kMillionsWithDecimalsFormat(current)}`,
                `Variance: ${sign}${kMillionsWithDecimalsFormat(variance)} (${sign}${variancePct.toFixed(1)}%)`
              ].join('\n')
            };
          }
        },
        label: {
          enabled: true,
          fontSize: 10,
          fontWeight: 'bold',
          color: '#fff',
          formatter: (params) => {
            // Show original budget value inside the dark blue bar - rounded like tooltip
            const val = params.value || params.datum?.originalBudget || 0;
            if (val >= 1000000000) return `$${(val / 1000000000).toFixed(1)}B`;
            if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
            if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
            return `$${Math.round(val)}`;
          },
          placement: 'inside'
        }
      },
      {
        type: 'column',
        xKey: 'category', 
        yKey: 'overage',
        yName: 'Overage',
        stacked: true,
        grouped: false, // CRITICAL: actually stack on the same x position
        fill: '#00D4FF',
        fillOpacity: 1.0,
        stroke: '#00A8CC',
        strokeWidth: 3,
        maxWidth: 28,
        tooltip: {
          renderer: (params) => {
            const original = params.datum.originalBudget;
            const current = original + params.datum.overage;
            const variance = params.datum.variance;
            const variancePct = params.datum.variancePct;
            const sign = variance >= 0 ? '+' : '';
            
            return {
              title: params.datum.category,
              content: [
                `Original: ${kMillionsWithDecimalsFormat(original)}`,
                `Current: ${kMillionsWithDecimalsFormat(current)}`,
                `Variance: ${sign}${kMillionsWithDecimalsFormat(variance)} (${sign}${variancePct.toFixed(1)}%)`
              ].join('\n')
            };
          }
        },
        label: {
          enabled: true,
          fontSize: 12,
          fontWeight: 'bold',
          color: '#003366',
          placement: 'top',  // Place at top of the stacked series
          padding: 10,  // Add 10px padding above the bar
          formatter: (params) => {
            // Determine if this is HC or SC based on the overage value
            const isHC = Math.abs(params.value - hcOverage) < 10;
            const originalBudget = isHC ? hcTotalOriginalBudget.value : scTotalOriginalBudget.value;
            
            if (!originalBudget || originalBudget === 0) {
              return '+0.0%';
            }
            
            const variancePct = (params.value / originalBudget) * 100;
            const sign = variancePct >= 0 ? '+' : '';
            return `${sign}${variancePct.toFixed(1)}%`;
          }
        }
      }
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          fontSize: 12,
          fontWeight: 'bold',
          color: '#333',
          padding: 6
        }
      },
      {
        type: 'number',
        position: 'left',
        label: {
          fontSize: 10,
          formatter: (params) => {
            const val = params.value;
            if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
            if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
            return `$${val}`;
          },
          color: '#666'
        },
        gridStyle: [{
          stroke: 'rgba(200,200,200,0.3)',
          lineDash: [4, 4]
        }],
        nice: false,  // Disable "nice" rounding so our max value is respected exactly
        // Set max to 140% of tallest bar to leave MUCH more room for labels above
        max: Math.max(
          hcTotalOriginalBudget.value + hcOverage,
          scTotalOriginalBudget.value + scOverage
        ) * 1.4
      }
    ],
    background: {
      fill: 'transparent'
    },
    legend: {
      enabled: false
    }
  };
});

const router = useRouter()
const emit = defineEmits(['updateBreadCrumb'])
const baseBreadCrumb = 'Comp Search | Comp Search Details'
const reroute = (path, label) => {
    emit('updateBreadCrumb', baseBreadCrumb + " | " + label)
    router.push({ path: path })
}

const extractDate = (dateStr) => {
    if (dateStr) {
        const dateParts = dateStr.split('-');
        return dateParts[0];
    } else {
        return '';
    }
}

// // TODO: Comment out the Schedule AG Grid related codes
// // For Schedule Gant Chart - With Quarters (Start)
// let aggregateData = {};
// let processedAggregateData = {};
// let lastProjectsQuery = [];

// let isNoSelectedProject = ref(false);
// let isNoDataFetch = ref(false);

// const dataShow = ref([]);

// let localColumnDefs = [];

// function updateData() {
//     if (currentProjects.value.length !== 0) {
//         fetchGridData()
//     } else {
//         isNoSelectedProject.value = false
//     }
// }

// // this is fetching data for all cost types
// // Process the data for the summed data
// function fetchGridData() {
//     const selectedIds = Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).join(',')
//     // Do *not* requery data
//     if (lastProjectsQuery === selectedIds) {
//         return
//     } else {
//         lastProjectsQuery = selectedIds
//     }

//     if (selectedIds.length === 0) {
//         isNoSelectedProject.value = true
//         return
//     } else {
//         isNoSelectedProject.value = false
//         axiosQuery(
//             `${apiBase}scope_of_works/summed_dates?project_ids=${selectedIds}`,
//             authToken,
//             (data) => {
//                 if (data.length == 0) {
//                     isNoDataFetch.value = true
//                 } else {
//                     aggregateData = {}
//                     processedAggregateData = {}
//                     data?.forEach((row) => {
//                         if (row.itemHierarchy && row.actual_project_months) {
//                             let quarters = {}
//                             Array(maxQuarters * 3).fill(0).forEach((_, quarter) => {
//                                 // TODO || parts here are to aid in testing. Need more projected start dates/months
//                                 let projectedStartMonth = parseFloat(row.projected_months_into_project_start || row.actual_months_into_project_start)
//                                 // TODO uncertain why some are < 0
//                                 if (projectedStartMonth < 0) projectedStartMonth = 1
//                                 let projectedEndMonth = projectedStartMonth + parseFloat(row.projected_project_months || row.actual_project_months)
//                                 let projectedContribution = projectedStartMonth <= (quarter + 1) && projectedEndMonth >= quarter + 2 ? 1 : 0

//                                 let actualStartMonth = parseFloat(row.actual_months_into_project_start)
//                                 let actualEndMonth = actualStartMonth + parseFloat(row.actual_project_months)
//                                 let actualContribution = actualStartMonth <= (quarter + 1) && actualEndMonth >= quarter + 2 ? 1 : 0

//                                 // Get percentage into month -- divided into 10 pieces
//                                 if (parseFloat(numberFormat(projectedStartMonth, 0)) === quarter) {
//                                     projectedContribution = decimalPart(projectedStartMonth)
//                                     // quarters[`month_length_change_${month}_projected_start`] = true
//                                 }
//                                 if (parseFloat(numberFormat(actualStartMonth, 0)) === quarter) {
//                                     actualContribution = decimalPart(actualStartMonth)
//                                     // quarters[`month_length_change_${month}_actual_start`] = true
//                                 }
//                                 // If we are on the last month....
//                                 if (parseFloat(numberFormat(projectedContribution, 0)) === quarter + 1) {
//                                     projectedContribution = decimalPart(projectedContribution)
//                                 }
//                                 if (parseFloat(numberFormat(actualContribution, 0)) === quarter + 1) {
//                                     actualContribution = decimalPart(actualContribution)
//                                 }
//                                 quarters[`quarter_length_change_${quarter}`] = [projectedContribution, actualContribution]
//                             })

//                             cleanupHierarchyNames(row.itemHierarchy).forEach(term => {
//                                 aggregateData[term] ||= []
//                                 aggregateData[term].push({
//                                     dda_term: term,
//                                     projected_project_months: numberFormat(row.projected_project_months),
//                                     actual_project_months: numberFormat(row.actual_project_months),
//                                     projected_months_into_project_start: numberFormat(row.projected_months_into_project_start),
//                                     actual_months_into_project_start: numberFormat(row.actual_months_into_project_start),
//                                     variance: numberFormat((row.actual_project_months || 0) - (row.projected_project_months || 0)),
//                                     ...quarters
//                                 })
//                             })
//                         }
//                     });

//                     // Process the aggregate data for the subtotal/total rows in the itemHierarchy
//                     processRollupData()
//                     // Process the averaged data for the leaf nodes
//                     processLeafNodeData(data)
//                     isNoDataFetch.value = false
//                 }

//                 isNoSelectedProject.value = true
//             },
//             errorStore
//         );
//     }

// }

// // The goal is to store in the rolled up terms:
// // 1. minimum months into project start, as we want to see the earliest it is projected to or actually started
// // 2. maximum length of project. This needs to be the latest the project could/did end minus the earliest it could/did start
// function processRollupData() {
//     Object.keys(aggregateData).forEach(term => {
//         let minActualMonthsIntoProject = 0.0;
//         let minProjectedMonthsIntoProject = 0.0;
//         let maxActualProjectMonthsLength = 0.0;
//         let maxProjectedProjectMonthsLength = 0.0;

//         if (aggregateData[term]?.length) {
//             aggregateData[term].forEach(value => {
//                 if (minActualMonthsIntoProject) {
//                     minActualMonthsIntoProject = Math.min(minActualMonthsIntoProject, parseFloat(value.actual_months_into_project_start || 0.0))
//                 } else {
//                     minActualMonthsIntoProject = parseFloat(value.actual_months_into_project_start || 0.0)
//                 }
//             })

//             aggregateData[term].forEach(value => {
//                 if (minProjectedMonthsIntoProject) {
//                     minProjectedMonthsIntoProject = Math.min(minProjectedMonthsIntoProject, parseFloat(value.projected_months_into_project_start || 0.0))
//                 } else {
//                     minProjectedMonthsIntoProject = parseFloat(value.projected_months_into_project_start || 0.0)
//                 }
//             })

//             aggregateData[term].forEach(value => {
//                 const actualProjectLength = parseFloat(value.actual_months_into_project_start || 0.0) + parseFloat(value.actual_project_months || 0.0)
//                 if (maxActualProjectMonthsLength) {
//                     maxActualProjectMonthsLength = Math.max(maxActualProjectMonthsLength, actualProjectLength)
//                 } else {
//                     maxActualProjectMonthsLength = actualProjectLength
//                 }
//             })
//             maxActualProjectMonthsLength -= minActualMonthsIntoProject

//             aggregateData[term].forEach(value => {
//                 const projectedProjectLength = parseFloat(value.projected_months_into_project_start || 0.0) + parseFloat(value.projected_project_months || 0.0)
//                 if (maxProjectedProjectMonthsLength) {
//                     maxProjectedProjectMonthsLength = Math.max(maxProjectedProjectMonthsLength, projectedProjectLength)
//                 } else {
//                     maxProjectedProjectMonthsLength = projectedProjectLength
//                 }
//             })
//             maxProjectedProjectMonthsLength -= minProjectedMonthsIntoProject

//             // TODO minor hack for now to make data look good since we don't always have projected months
//             if (!maxProjectedProjectMonthsLength) { maxProjectedProjectMonthsLength = maxActualProjectMonthsLength }
//         }

//         processedAggregateData[term] = {
//             minActualMonthsIntoProject: minActualMonthsIntoProject,
//             minProjectedMonthsIntoProject: minProjectedMonthsIntoProject,
//             maxActualProjectMonthsLength: maxActualProjectMonthsLength,
//             maxProjectedProjectMonthsLength: maxProjectedProjectMonthsLength
//         }
//     })
// }

// // Process the data for the individual nodes
// let isAveragedDataLoaded = ref(false)
// function processLeafNodeData(data) {
//     data?.forEach((row) => {
//         if (row.itemHierarchy && row.actual_project_months) {
//             let quarters = {}
//             Array(maxQuarters / 3).fill(0).forEach((_, quarter) => {
//                 // TODO || parts here are to aid in testing. Need more projected start dates/months
//                 let projectedStartMonth = parseFloat(row.projected_months_into_project_start || row.actual_months_into_project_start)
//                 let projectedProjectMonths = parseFloat(row.projected_project_months || row.actual_project_months)
//                 if (projectedStartMonth < 0) projectedStartMonth = 1

//                 let actualStartMonth = parseFloat(row.actual_months_into_project_start)
//                 let actualProjectMonths = parseFloat(row.actual_project_months)

//                 // Each month of the Quarter
//                 let [projectedWidthPctOne, isProjectedRightAlignedOne] = quarterPercent(quarter / 3, projectedStartMonth, projectedProjectMonths)
//                 let [actualWidthPctOne, isActualRightAlignedOne] = quarterPercent(quarter / 3, actualStartMonth, actualProjectMonths)
//                 let [projectedWidthPctTwo, isProjectedRightAlignedTwo] = quarterPercent(quarter / 3 + 1, projectedStartMonth, projectedProjectMonths)
//                 let [actualWidthPctTwo, isActualRightAlignedTwo] = quarterPercent(quarter / 3 + 2, actualStartMonth, actualProjectMonths)
//                 let [projectedWidthPctThree, isProjectedRightAlignedThree] = quarterPercent(quarter / 3 + 2, projectedStartMonth, projectedProjectMonths)
//                 let [actualWidthPctThree, isActualRightAlignedThree] = quarterPercent(quarter / 3 + 2, actualStartMonth, actualProjectMonths)

//                 // Calculate full quarter projected width & percent
//                 let projectedWidthPct = projectedWidthPctOne
//                 let isProjectedRightAligned = isProjectedRightAlignedOne
//                 if (projectedWidthPctOne === 1) {
//                     projectedWidthPct += projectedWidthPctTwo
//                     isProjectedRightAligned = isProjectedRightAlignedTwo
//                 }
//                 if (projectedWidthPctTwo === 1) {
//                     projectedWidthPct += projectedWidthPctThree
//                     isProjectedRightAligned = isProjectedRightAlignedThree
//                 }
//                 projectedWidthPct = projectedWidthPct / 3.0

//                 // Calculate full quarter actual width & percent
//                 let actualWidthPct = actualWidthPctOne
//                 let isActualRightAligned = isActualRightAlignedOne
//                 if (actualWidthPctOne === 1) {
//                     actualWidthPct += actualWidthPctTwo
//                     isActualRightAligned = isActualRightAlignedTwo
//                 }
//                 if (actualWidthPctTwo === 1) {
//                     actualWidthPct += actualWidthPctThree
//                     isActualRightAligned = isActualRightAlignedThree
//                 }
//                 actualWidthPct = actualWidthPct / 3.0

//                 quarters[`quarter_length_change_${quarter}`] = [projectedWidthPct, actualWidthPct, isProjectedRightAligned, isActualRightAligned,
//                     projectedProjectMonths / 3.0, actualProjectMonths / 3.0, projectedStartMonth / 3.0, actualStartMonth / 3.0]
//             })
//             dataShow.value.push({
//                 ...{
//                     id: row.id,
//                     project_id: row.project_id,
//                     dda_term_id: row.dda_term_id,
//                     hiddenHierarchy: cleanupHierarchyNames(row.itemHierarchy),
//                     itemHierarchy: cleanupHierarchyNames(row.itemHierarchy),
//                     projected_project_months: formatDecimals(row.projected_project_months / 3.0),
//                     actual_project_months: formatDecimals(row.actual_project_months / 3.0),
//                     projected_months_into_project_start: formatDecimals(row.projected_months_into_project_start / 3.0),
//                     actual_months_into_project_start: formatDecimals(row.actual_months_into_project_start / 3.0),
//                     variance: formatDecimals(((row.actual_project_months || 0) - (row.projected_project_months || 0)) / 3.0),
//                 }, ...quarters
//             })
//         }
//     });

//     // Use with the custom filters but still having the same effect only with just the Hard Cost included
//     // Define the values to include
//     const includeValues = ['Excavation & Foundation', 'Superstructure', 'Enclosure', 'Rough-Ins', 'Finishes'];

//     // New array to hold filtered results
//     let filteredResults = [];

//     includeValues.forEach(value => {
//         // Find the first item where itemHierarchy contains the value
//         const foundItem = dataShow.value.find(item => item.itemHierarchy.includes(value));
//         // Assuming each item has a unique identifier 'id' for this example
//         if (foundItem && !filteredResults.some(item => item.id === foundItem.id)) {
//             filteredResults.push(foundItem);
//         }
//     });

//     // Strings to remove from itemHierarchy
//     const stringsToRemove = ['Trade Costs  (From Construction Manager)', 'Interior Fit-Out'];

//     // Modify itemHierarchy for each item in filteredResults
//     filteredResults = filteredResults.map(item => {
//         item.itemHierarchy = item.itemHierarchy.filter(hierarchyItem =>
//             !stringsToRemove.includes(hierarchyItem)
//         );
//         return item;
//     });

//     // Replace dataShow.value with the modified filtered results
//     dataShow.value = filteredResults;

//     isAveragedDataLoaded.value = true
// }

// const maxQuarters = 48; // or 16 4 quarters per year over 4 years
// let columnGroup = {};
// Array(maxQuarters / 2).fill(0).forEach((_, quarter) => {
//     if (quarter % 4 === 0) { // Start a new year group every 4 quarters
//         if (quarter > 0) {
//             // Save old column defs
//             localColumnDefs.push(columnGroup);
//         }
//         columnGroup = {
//             groupId: `year_${Math.floor(quarter / 4) + 1}`,
//             headerName: `Year ${Math.floor(quarter / 4) + 1}`,
//             menuTabs: ['columnsMenuTab'],
//             children: []
//         };
//     }
//     // Adjusting for quarters instead of months
//     columnGroup.children.push({
//         headerName: `Q${quarter % 4 + 1}`,
//         field: `quarter_length_change_${quarter}`,
//         cellRenderer: 'agGroupCellRenderer',
//         cellRendererParams: {
//             suppressCount: true,
//             checkbox: true,
//             innerRenderer: 'GanttCellRenderer',
//             suppressDoubleClickExpand: true,
//             suppressEnterExpand: true,
//         },
//         aggFunc: (params => quarterAggregate(params, quarter)), // Adjusted to a new aggregation function for quarters
//         editable: false,
//         suppressMenu: true,
//         sortable: false,
//         suppressMovable: true,
//         maxWidth: 34,
//         headerClass: quarter % 4 === 0 ? 'schedule-sparkline-header year' : 'schedule-sparkline-header',
//         cellClass: quarter % 4 === 0 ? 'schedule-sparkline year' : 'schedule-sparkline',
//         cellRendererSelector: (params) => {
//             return {
//                 component: GanttCellRenderer,
//                 params: {
//                     topWidthPct: parseFloat(params.value[0]),
//                     bottomWidthPct: parseFloat(params.value[1]),
//                     isTopRightAligned: params.value[2],
//                     isBottomRightAligned: params.value[3],
//                     projectedProjectMonths: formatDecimals(parseFloat(params.value[4]), 1) / 3.0,
//                     actualProjectMonths: formatDecimals(parseFloat(params.value[5]), 1) / 3.0,
//                     // -0.00 sometimes crops up here...
//                     projectedMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[6])), 1) / 3.0,
//                     actualMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[7])), 1) / 3.0,
//                 },
//             }
//         },
//         filter: false,
//     });
// });

// // Aggregate to show in column for this month
// // Depending on which data is passed in, the goal is the get:
// // 1. minimum months into project start, as we want to see the earliest it is projected to or actually started
// // 2. maximum length of project. This needs to be the latest the project could/did end minus the earliest it could/did start
// function quarterAggregate(params, month) {
//     let data = processedAggregateData[params.rowNode.key]

//     if (data) {
//         let [projectedWidthPct, isProjectedRightAligned] = quarterPercent(month, data.minProjectedMonthsIntoProject / 3.0, data.maxProjectedProjectMonthsLength / 3.0)
//         let [actualWidthPct, isActualRightAligned] = quarterPercent(month, data.minActualMonthsIntoProject / 3.0, data.maxActualProjectMonthsLength / 3.0)

//         return [projectedWidthPct, actualWidthPct, isProjectedRightAligned, isActualRightAligned,
//             data.maxProjectedProjectMonthsLength / 3.0, data.maxActualProjectMonthsLength / 3.0, data.minProjectedMonthsIntoProject / 3.0, data.minActualMonthsIntoProject / 3.0]
//     } else {
//         return Array(8).fill(0);
//     }
// }

// // Calculates how much of a band to show of a project length
// function quarterPercent(quarter, quartersIntoProject, projectquarterLength) {
//     let widthPct = 0
//     let isRightAligned = false

//     if (quartersIntoProject < quarter + 1) {
//         if (quartersIntoProject > quarter) {
//             // spans part of the first column cell
//             widthPct = (quarter + 1) - quartersIntoProject
//             isRightAligned = true
//         } else if (quartersIntoProject + projectquarterLength > quarter &&
//             quartersIntoProject + projectquarterLength < quarter + 1) {
//             // spans part of the end column cell
//             widthPct = (quartersIntoProject + projectquarterLength) - (quarter)
//         } else if (quartersIntoProject <= (quarter + 2) && quartersIntoProject + projectquarterLength > quarter) {
//             widthPct = 1
//         }
//     }

//     return [widthPct, isRightAligned];
// }

// const tooltipStore = useGanttTooltipStore()
// let tooltipIsShow = ref(tooltipStore.getIsShow)
// let tooltipTop = ref(tooltipStore.getTop)
// let tooltipLeft = ref(tooltipStore.getLeft)
// let tooltipHtml = ref(tooltipStore.getHtml)
// tooltipStore.$subscribe(updateTooltip, { detached: false })
// function updateTooltip() {
//     tooltipIsShow.value = tooltipStore.getIsShow
//     tooltipTop.value = tooltipStore.getTop
//     tooltipLeft.value = tooltipStore.getLeft
//     tooltipHtml.value = tooltipStore.getHtml
// }

// // For Schedule Gant Chart - With Quarters (End)


// Watch for changes in mathSummaryStore.getViewModeBy
watch(
  () => mathSummaryStore.getViewModeBy,
  (newVal) => {
    if (newVal) {
      viewByTGU.value = newVal;
    }
  },
  { immediate: true }
);

// 🔥 NEW: Recalculate chart values when view mode changes
function recalculateChartValuesForViewMode() {
    const divisor = mathSummaryStore.getViewModeDivisor || 1;
    console.log('🔥 CompSummary: Recalculating chart values with divisor:', divisor);
    
    // Recalculate all chart values by dividing base values by the divisor
    lcTotalRevisedBudget.value = baseLcTotalRevisedBudget.value / divisor;
    hcTotalRevisedBudget.value = baseHcTotalRevisedBudget.value / divisor;
    scTotalRevisedBudget.value = baseScTotalRevisedBudget.value / divisor;
    miscFinancingTotalRevisedBudget.value = baseMiscFinancingTotalRevisedBudget.value / divisor;
    contingenciesTotalRevisedBudget.value = baseContingenciesTotalRevisedBudget.value / divisor;

    lcTotalOriginalBudget.value = baseLcTotalOriginalBudget.value / divisor;
    hcTotalOriginalBudget.value = baseHcTotalOriginalBudget.value / divisor;
    scTotalOriginalBudget.value = baseScTotalOriginalBudget.value / divisor;
    miscFinancingTotalOriginalBudget.value = baseMiscFinancingTotalOriginalBudget.value / divisor;
    contingenciesTotalOriginalBudget.value = baseContingenciesTotalOriginalBudget.value / divisor;
    
    // Recalculate variances (these stay the same in absolute terms, but percentages remain the same)
    lcTotalVariance.value = lcTotalRevisedBudget.value - lcTotalOriginalBudget.value;
    hcTotalVariance.value = hcTotalRevisedBudget.value - hcTotalOriginalBudget.value;
    scTotalVariance.value = scTotalRevisedBudget.value - scTotalOriginalBudget.value;
    miscFinancingTotalVariance.value = miscFinancingTotalRevisedBudget.value - miscFinancingTotalOriginalBudget.value;
    contingenciesTotalVariance.value = contingenciesTotalRevisedBudget.value - contingenciesTotalOriginalBudget.value;
    
    // Variance percentages remain the same regardless of divisor
    lcVariancePercent.value = lcTotalOriginalBudget.value > 0 ? (lcTotalRevisedBudget.value - lcTotalOriginalBudget.value) / lcTotalOriginalBudget.value : 0;
    hcVariancePercent.value = hcTotalOriginalBudget.value > 0 ? (hcTotalRevisedBudget.value - hcTotalOriginalBudget.value) / hcTotalOriginalBudget.value : 0;
    scVariancePercent.value = scTotalOriginalBudget.value > 0 ? (scTotalRevisedBudget.value - scTotalOriginalBudget.value) / scTotalOriginalBudget.value : 0;
    miscFinancingVariancePercent.value = miscFinancingTotalOriginalBudget.value > 0 ? (miscFinancingTotalRevisedBudget.value - miscFinancingTotalOriginalBudget.value) / miscFinancingTotalOriginalBudget.value : 0;
    contingenciesVariancePercent.value = contingenciesTotalOriginalBudget.value > 0 ? (contingenciesTotalRevisedBudget.value - contingenciesTotalOriginalBudget.value) / contingenciesTotalOriginalBudget.value : 0;
    
    // Update doughnut chart data
    doughnutChartData.value = [
        lcTotalRevisedBudget.value,
        hcTotalRevisedBudget.value,
        scTotalRevisedBudget.value,
        miscFinancingTotalRevisedBudget.value,
        contingenciesTotalRevisedBudget.value
    ];
    
    console.log('🔥 CompSummary: Recalculated chart values:', {
        hc: { original: hcTotalOriginalBudget.value, current: hcTotalRevisedBudget.value },
        sc: { original: scTotalOriginalBudget.value, current: scTotalRevisedBudget.value },
        divisor: divisor
    });
}

function setViewMode() {
    if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_GSF) {
        mathSummaryStore.setViewModeDivisor(avgTotalGSF.value)
        viewModeLabel.value='/gsf'
        doughnutViewMode.value = 'gsf'
    } else if (viewByTGU.value === VIEW_MODES_TGU.VIEW_BY_UNIT) {
        mathSummaryStore.setViewModeDivisor(avgTotalUnits.value)
        viewModeLabel.value='/unit'
        doughnutViewMode.value = 'unit'
    } else {
        mathSummaryStore.setViewModeDivisor(1)
        viewModeLabel.value=''
        doughnutViewMode.value = 'total'
    }
    mathSummaryStore.setViewModeBy(viewByTGU.value)
    
    // 🔥 NEW: Recalculate chart values with the new divisor
    recalculateChartValuesForViewMode();
}

watch(() => compSetStore.includedProjects, async (newProjects) => {
  currentProjects.value = await fetchProjectImages(newProjects);
  noSelectedCompSet.value = currentProjects.value.length === 0;
});

async function fetchProjectImages(projects) {
  const updatedProjects = [];
  for (const project of projects) {
    const imageUrl = await fetchImage(project.id);
    // Spread into a plain object — mutating the reactive store object directly
    // re-triggers $subscribe on every image fetch, causing an infinite loop.
    updatedProjects.push({ ...project, image_url: imageUrl });
  }
  return updatedProjects;
}

const doughnutChartRef = ref(null);

// Expose the chart instance for Cypress
const getDoughnutChartInstance = () => {
  return doughnutChartRef.value?.chart; // Access the chart instance via proxy
};

const barChartRef = ref(null);

// Expose the bar chart instance for Cypress
const getBarChartInstance = () => {
  return barChartRef.value?.chart; // Access the chart instance via proxy
};

// Expose properties and methods to parent components
defineExpose({
  showModal,
  toggleModal
});

</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

.side-panel-left {
    position: fixed;
    display: flex;
    top: 50px;
    left: 310px; /* Position after first panel */
    height: 100vh;
    background-color: $white1;
    transition: width 0.5s ease;
    z-index: 4;
    box-shadow: 6px 0 18px rgba(0, 0, 0, 0.06);
}

/* Override ExpandCollapse positioning for second panel */
.side-panel-left .expand-collapse.right {
    right: -16px !important;
    left: auto !important;
    z-index: 10 !important; /* Ensure button appears above panel bounds */
}

/* HIDE collapse arrows to prevent reopening issue */
.side-panel-left .expand-collapse {
    display: none !important;
}

.comp-summary-title {
    font-size: 16px;
    font-family: 'Inter';
    font-weight: bold;
    color: $navyBlue;
    padding: 20px 0 10px 0;
}

.side-panel-left.open {
    width: 750px;
    transition: width 0.5s ease;
}

.side-panel-left.closed {
    width: 20px;
    transition: width 0.5s ease;
}

/* Hide expand/collapse button when panel is closed */
.side-panel-left.closed .expand-collapse {
    display: none !important;
}

.hidden {
    display: none;
}

.panel {
    width: 750px;
    box-shadow: 2px 0 18px #BEBFBF;
    overflow: visible; /* Allow collapse button to appear outside bounds */
}

.comp-summary {
    padding: 0.25rem 1.5rem;
    font-family: 'Inter';
    overflow-y: scroll;
}

.content {
    /* No special direction needed for left panel */
}

.no-selected-data {
    font-size: 12px;
    color: $navyBlue;
}

.comp-summary::-webkit-scrollbar {
    width: 5px;
    height: 8px;
    background-color: $white1;
}

.comp-summary::-webkit-scrollbar-thumb {
    background: $lightGray1;
}

h4 {
    color: $navyBlue;
}

.header-search {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.left-div .right-div {
    flex: 1;
}

.divider {
    border: 1px solid $lightGray1;
    margin: 5px 0px;
}

.divider-comp-set {
    width: 100%;
    display: flex;
    justify-content: center;

    div {
        border: 1px solid lightgray;
        width: 50%;
        margin: 5px 0;
    }
}

.accordion-panel-header {
    display: flex;
    align-items: center;
    position: relative;
    width: 97%;
    height: 27px;

    label {
        font-size: 14px;
    }
}

.budget-overview-container,
.schedule-overview-container,
.active-compset-container {
    display: flex;
    flex-direction: column;
    padding: 15px 0;
    position: relative;

    label {
        margin-bottom: 5px;
        color: $navyBlue;
        font-size: 14px;
        font-weight: bold;
    }
}

.comp-set-counter {
    color: $navyBlue;
    padding-left: 5px;
    font-weight: bold;
}

.budget-overview-container,
.schedule-overview-container {
    img {
        width: 100%;
        height: 100%
    }
}

.bar-chart-legend-revised {
    text-align: right;
    font-size: 10px;
    color: $lightBlue1;

    span:first-child {
        padding-right: 12px;
    }

    span:nth-child(2) {
        padding-right: 7px;
    }
}

// Doughnut Legend
.doughnut-chart-legend {
    font-size: 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .acquisition-legend {
        margin: 5px 0;

        .detail-container {
            display: flex;
            flex-direction: row;
            justify-content: space-evenly;
            color: $darkGray1
        }

        .vertical-divider {
            border-left: 5px solid $darkGray1;
            margin: 0 5px;
        }
    }

    .hard-legend {
        margin: 5px 0;

        .detail-container {
            display: flex;
            flex-direction: row;
            justify-content: space-evenly;
            color: $brightBlue1;
        }

        .vertical-divider {
            border-left: 5px solid $brightBlue1;
            margin: 0 5px;
        }
    }

    .soft-legend {
        margin: 5px 0;

        .detail-container {
            display: flex;
            flex-direction: row;
            justify-content: space-evenly;
            color: $mediumBlue1;
        }

        .vertical-divider {
            border-left: 5px solid $mediumBlue1;
            margin: 0 5px;
        }
    }

    .misc-finance-legend {
        margin: 5px 0;

        .detail-container {
            display: flex;
            flex-direction: row;
            justify-content: space-evenly;
            color: $mediumGray1;
        }

        .vertical-divider {
            border-left: 5px solid $mediumGray1;
            margin: 0 5px;
        }
    }

    .contingencies-legend {
        margin: 5px 0;

        .detail-container {
            display: flex;
            flex-direction: row;
            justify-content: space-evenly;
            color: #d3d3d3;
        }

        .vertical-divider {
            border-left: 5px solid #d3d3d3;
            margin: 0 5px;
        }
    }
}

.view-by-container {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    font-size: 12px;
    color: $navyBlue;
    width: 45%;

    div:first-child select {
        width: 95%;
    }
}

.active-compset-container .accordion-panel-header {
    label {
        margin-bottom: 5px;
        color: $navyBlue;
        font-size: 16px;
        font-weight: bold;
    }
}

.active-compset-container {
    .active-compset-label {
        color: $darkGray2;
        font-size: 12px;
        padding: 0.5rem 0;

        span {
            color: $brightBlue1;
            padding: 0px 5px;
        }
    }
}

.active-compset-details-container {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 5px;
    color: $navyBlue;

    .property-address {
        font-size: 14px;
        font-weight: bold;
        padding: 5px 0px;
        display: flex;
        justify-content: space-between;
    }

    .property-thumbnail {
        margin: 5px 28px 5px 5px;
        border: .5px solid $lightGray1;
        border-radius: 2px;

        img {
            width: 100px;
            height: 100px;
        }
    }

    .property-details {
        display: flex;
        flex-direction: row;
        flex: 2;

        .property-building-info,
        .property-construction-info {
            font-size: 12px;
            width: 100%;
        }

        .property-building-label,
        .property-construction-label {
            font-size: 13px;
            font-weight: bolder;
            padding-bottom: 5px;
            white-space: nowrap;
        }

        .property-building-info {
            padding-right: 1rem;
        }

        .property-info {
            padding-left: 5px;
        }

        .property-info-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 2px 0px;

            .property-info-value {
                text-align: right;
            }
        }
    }
}

.property-info {
    width: 73%;
}

.custom-select {
    width: 100%;
    position: relative;
    font-family: inherit;
    display: flex;
    align-items: center;
    font-size: 12px;
    margin-bottom: 5px;
    background: none;

    select {
        border: 0;
        color: $brightBlue1;
        background: none;
    }

    select:focus {
        border-color: $brightBlue1;
        outline: $brightBlue1;
    }
}

ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
}

// Action Buttons
.action-btn-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-top: 10px;
    margin-bottom: 8vh;
}

.primary-action-button {
    background-color: $mediumBlue2;
    color: white;
    border: 0;
    border-radius: 4px;
    margin: 8px;
    cursor: pointer;
    padding: 10px;
    font-size: 12px;
}

.secondary-action-button {
    background-color: transparent;
    color: $brightBlue1;
    border: 0;
    cursor: pointer;
    padding: 5px;
    font-size: 12px;
    margin-right: 15px;
}

.secondary-action-button:hover {
    text-decoration: underline;
}

// Charts
.budget-chart-container {
    display: flex;
    flex-direction: row;
    gap: 16px;
    width: 100%;
}

.doughnut-chart-container {
    flex: 0 0 auto;
    padding: 16px;
    background: $white1;
    border: 1px solid $lightGray1;
    border-radius: 12px;
    width: 380px;
}

.bar-chart-container {
    flex: 1 1 auto;
    padding: 16px;
    background: $white1;
    border: 1px solid $lightGray1;
    border-radius: 12px;
    min-width: 280px;
    max-width: 340px;
}

.chart-label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #333;
}

.chart-content {
    display: flex;
    align-items: center;
}

// Donut chart specific styles
.doughnut-chart-body {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 20px !important;
}

.donut-wrapper {
    position: relative !important;
    width: 180px !important;
    height: 180px !important;
    flex-shrink: 0 !important;
}

.donut-center {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    text-align: center !important;
    pointer-events: none !important;
}

.donut-total {
    font-size: 20px;
    font-weight: 700;
    color: #18a27f;
    line-height: 1.2;
}

.donut-label {
    font-size: 10px;
    color: #666;
    line-height: 1.3;
    margin-top: 2px;
}

.donut-legend {
    display: flex !important;
    flex-direction: column !important;
    gap: 10px !important;
    font-size: 12px;
}

.legend-row {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
}

.legend-swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    flex-shrink: 0;
}

.legend-value {
    margin-left: auto;
    font-weight: 600;
    color: #333;
    padding-left: 8px;
}

.legend-swatch.acquisition { background: #52596D; }
.legend-swatch.hard        { background: #4A5FD5; }
.legend-swatch.soft        { background: #4A8FD0; }
.legend-swatch.misc        { background: #8A92A9; }

.go-to-button {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    margin-top: 12px;
}

// Schedule Gantt Chart Styles
// .schedule-gantt .schedule-sparkline {
//     // keep border, but as narrow as possible
//     border-left-width: 0.5px;
//     border-right-width: 0.5px;
//     outline: 0;
// }

// .schedule-gantt .schedule-sparkline.year {
//     // keep border slightly wider at the year cutoff
//     border-left-width: 1.5px;
// }

// .schedule-gantt .schedule-sparkline-header {
//     // keep sparklines flush against cells
//     box-sizing: content-box;
//     padding-right: 0;
//     padding-left: 4px;
// }

// .schedule-gantt .schedule-sparkline-header.year {
//     // keep border slightly wider at the year cutoff
//     border-left-width: 1.5px;
// }

.active-compset-label span.disabled-button {
    color: $lightGray1;
}

.disabled-button {
    color: $lightGray1;
    cursor: not-allowed;

    &:hover {
        text-decoration: none;
    }
}

/** Schedule Card - Start */
.schedule-card {
    display: flex;
    border-radius: 8px;
    overflow: hidden; /* Ensures the background colors of sections stay within the card */
    width: 100%;
    font-weight: bold;

    .text-section,
    .numbers-section {
        display: flex;
        flex-direction: column;
        justify-content: space-between; /* Ensures even spacing between items */
        padding: 20px;
    }

    .text-section {
        background-color: $darkWhite1; /* Light grey background */
        flex: 1; /* Makes this section flexible to align with the numbers-section */
    }

    .text-section h1 {
        font-size: 1.25rem;
        color: $darkBlue1;
        margin: 0 0 10px 0;
    }

    .text-section p {
        margin: 0;
        font-size: 1rem;
        color: $darkBlue1;
    }

    .text-section .variance {
        color: $lightBlue1;
        font-weight: bold;
        padding: 10px 0 10px 0;
    }

    .numbers-section {
        background-color: $lightBlue2; /* Light blue background */
        text-align: right;
        flex: 0.5; /* Adjust this ratio to balance width between sections */
        position: relative;
    }

    .numbers-section p {
        margin: 0;
        font-size: 1.5rem;
        font-weight: bold;
        display: flex;
        justify-content: flex-end; /* Align numbers to the right */
    }

    .numbers-section h1 {
        font-size: 18px;
        margin: 0 0 10px 0;
        color: $darkBlue1;
        text-align: right; /* Align title with the numbers */
    }

    .numbers-section .planned-number,
    .numbers-section .actual-number {
        color: $darkBlue1;
    }

    .numbers-section .variance-number {
        color: $lightBlue1;
        padding: 10px 0 10px 0;
    }
}
/** Schedule Card - End */

/** Spinner Container - Start */
.spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 59vh;
    width: 100%;
}
/** Spinner Container - End */

/** AG Charts Styles - Start */
// AG Chart wrapper - generic
.bar-chart-container .ag-chart-wrapper {
    width: 100%;
    height: 240px;
}

// Donut wrapper - OVERRIDE the ag-chart-wrapper for donut
.doughnut-chart-container .donut-wrapper {
    width: 180px !important;
    height: 180px !important;
}

.doughnut-chart-container .donut-wrapper .ag-chart {
    width: 180px !important;
    height: 180px !important;
}
/** AG Charts Styles - End */

/** Project Name Link Styles - Start */
.project-name-link {
    color: #4A5FD5;
    text-decoration: none;
    transition: color 0.2s ease;
}

.project-name-link:hover {
    color: #3b4db5;
    text-decoration: underline;
}

.project-name-link strong {
    color: inherit;
}
/** Project Name Link Styles - End */

/** Scope Search Styles - Start */
.scope-search-results {
    padding: 20px 0;
}

.scope-terms-display {
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f5f7fa;
    border-radius: 8px;
    
    strong {
        display: block;
        margin-bottom: 10px;
        color: $navyBlue;
        font-size: 14px;
    }
    
    .scope-badge {
        display: inline-block;
        padding: 6px 12px;
        margin: 4px;
        background-color: $brightBlue1;
        color: white;
        border-radius: 15px;
        font-size: 12px;
        font-weight: 500;
    }
}

.scope-chart-container {
    margin-bottom: 20px;
    
    .chart-label {
        font-size: 16px;
        font-weight: 600;
        color: $navyBlue;
        margin-bottom: 15px;
    }
    
    .scope-scatter-chart,
    .scope-stacked-bar-chart {
        width: 100%;
        height: 450px;
    }
}
/** Scope Search Styles - End */
</style>